import geoBbox from 'geojson-bbox';
import humanizeDuration from 'humanize-duration';
import { throttle } from 'lodash';
import { FunctionComponent, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Globe from 'react-globe.gl';

import countries from '@/json/countries.json';
import { CountryConsumption } from '@/services/RewindApi';

export interface RewindGlobeProps extends RewindSlideProps {
  maxCountryConsumption: {
    name: string;
    time: number;
  };
  consumptionPerCountry: Record<string, CountryConsumption>;
}

interface MapCenter {
  lat: number;
  lng: number;
  altitude: number;
}

interface GlobeNode {
  controls: () => {
    autoRotate: boolean;
    autoRotateSpeed: number;
    enableZoom: boolean;
  };
  pointOfView: (mapCentre: MapCenter, rotationSpeed: number) => void;
  resumeAnimation: () => void;
  pauseAnimation: () => void;
}

const RewindGlobe: FunctionComponent<RewindGlobeProps> = (props) => {
  const { maxCountryConsumption, consumptionPerCountry, active } = props;
  const [hoverD, setHoverD] = useState<Feature | null>(null);
  const [globeWidth, setGlobeWidth] = useState(850);
  const sbsOrange = useMemo(() => {
    return {
      r: 253,
      g: 183,
      b: 23,
    };
  }, []);

  /**
   * For Large screens override globe width depending on height
   *
   * @param value
   */
  function setGlobeWidthDesktop(value: number) {
    if (window.innerHeight < 600) {
      setGlobeWidth(340);
    } else if (window.innerHeight < 700) {
      setGlobeWidth(400);
    } else {
      setGlobeWidth(value);
    }
  }

  useEffect(() => {
    const resizeHandler = throttle(() => {
      switch (true) {
        case (window.innerWidth < 330):
          setGlobeWidth(310);
          break;

        case (window.innerWidth < 640):
          setGlobeWidth(340);
          break;

        case (window.innerWidth < 768):
          setGlobeWidth(400);
          break;

        case (window.innerWidth < 1280):
          setGlobeWidthDesktop(500);
          break;

        default:
          setGlobeWidth(630);
      }
    }, 250);

    resizeHandler();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const globeRef = useRef<GlobeNode>();

  useEffect(() => {
    if (globeRef.current) {
      const globe = globeRef.current;
      const controls = globe.controls();
      controls.autoRotate = !!active;
      controls.autoRotateSpeed = 0.35;
      controls.enableZoom = false;

      // Center the map on the country with most consumption time
      countries.features.forEach((country) => {
        if (maxCountryConsumption.name === country.properties.admin) {
          const bbox = geoBbox(country);
          const mapCenter = {
            lat: bbox[3],
            lng: bbox[2],
            altitude: 1.68, // The altitude of the point of view, this controls the zoom level
          };
          globe.pointOfView(mapCenter, 0);
        }
      });

      if (active) {
        globe.resumeAnimation();
      } else {
        globe.pauseAnimation();
      }
    }
  }, [active, maxCountryConsumption]);

  const colorScale = useCallback((consumptionTime: number) => {
    if (consumptionTime > 0) {
      const _percent = 100 - ((consumptionTime * 100) / maxCountryConsumption.time);

      // Countries with low consumption time show up almost white so we need to compress the color scale down.
      const maxPercent = 80;
      const percent = (maxPercent * _percent) / 100;

      const r = sbsOrange.r + Math.floor(((255 - sbsOrange.r) * percent) / 100);
      const g = sbsOrange.g + Math.floor(((255 - sbsOrange.g) * percent) / 100);
      const b = sbsOrange.b + Math.floor(((255 - sbsOrange.b) * percent) / 100);

      return `rgb(${r}, ${g}, ${b})`;
    }

    return 'rgba(255, 255, 255, 0.3)';
  }, [maxCountryConsumption, sbsOrange]);

  const getConsumptionTime = useCallback((countryData: Feature): number => {
    const countryName = countryData.properties.admin;
    if (countryName in consumptionPerCountry) {
      return consumptionPerCountry[countryName].time;
    }

    return 0;
  }, [consumptionPerCountry]);

  return (
    <Globe
      // @ts-ignore, test
      ref={globeRef}
      width={globeWidth}
      height={globeWidth}
      backgroundColor="rgba(0,0,0,0)"
      globeImageUrl="/images/earth-night-small.jpg"
      lineHoverPrecision={0}
      polygonsTransitionDuration={300}
      polygonSideColor={() => { return `rgba(${sbsOrange.r}, ${sbsOrange.g}, ${sbsOrange.b}, 0.08)`; }}
      polygonStrokeColor={() => { return '#111'; }}
      polygonsData={countries.features}
      onPolygonHover={(polygon) => {
        setHoverD(polygon as Feature);
      }}
      polygonCapColor={(countryData) => {
        return colorScale(getConsumptionTime(countryData as Feature));
      }}
      polygonAltitude={(countryData) => {
        const { properties: { admin: countryName } } = countryData as Feature;
        if (countryName in consumptionPerCountry) {
          // Elevate the hovered country if it has consumption data;
          return countryData === hoverD ? 0.06 : 0.04;
        }

        return 0.02;
      }}
      polygonLabel={(countryData) => {
        const { properties: { admin: countryName } } = countryData as Feature;

        if (countryName in consumptionPerCountry) {
          const { time, displayName } = consumptionPerCountry[countryName];
          const consumption = time * 60 * 1000;
          const humanizedConsumption = humanizeDuration(
            consumption,
            {
              language: 'shortEn',
              // @ts-ignore, this config key is not exposed by the official type
              languages: {
                shortEn: {
                  y: () => { return 'y'; },
                  w: () => { return 'mo'; },
                  d: () => { return 'd'; },
                  h: () => { return 'h'; },
                  m: () => { return 'm'; },
                  s: () => { return 's'; },
                  ms: () => { return 'ms'; },
                },
              },
              units: ['h', 'm'],
              delimiter: ' ',
              spacer: '',
            },
          );

          // Cannot use React components.
          // So we need to embed the SVG content directly.
          // Used https://vecta.io/nano to compress the original SVG file to reduce size by removing extraneous data.
          return `<div class="text-black p-5 bg-white h-[50px] min-w-[250px]
                flex flex-row gap-4 items-center justify-center
                rounded-2xl shadow-[0_2px_4px_0_rgba(165,165,165,0.5)]"
              >
                <span class="font-bold text-lg md:text-xl whitespace-nowrap">${displayName}</span>
                <span class="ml-auto text-base md:text-lg whitespace-nowrap flex flex-row gap-2 justify-center items-center">
                    <svg width="18px" height="18px" viewBox="0 0 18 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><title>time icon</title><g id="ui-design" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="countries-hover" transform="translate(-1479, -412)" fill="#1B2024" fill-rule="nonzero"><g id="hover" transform="translate(1312, 391)"><path d="M175.75,25 C175.335786,25 175,25.3357864 175,25.75 L175,30.5753572 C175,30.8388044 175.138224,31.0829345 175.364128,31.2184769 L179.132337,33.4794023 C179.474174,33.6845046 179.917461,33.5753634 180.125,33.235 C180.329498,32.8996232 180.2234,32.4619681 179.888023,32.2574701 C179.885457,32.2559056 179.882882,32.2543573 179.880296,32.2528255 L176.867692,30.4678578 C176.639769,30.332813 176.5,30.0875398 176.5,29.8226129 L176.5,25.75 C176.5,25.3357864 176.164214,25 175.75,25 Z M176,21 C171.03,21 167,25.03 167,30 C167,34.97 171.03,39 176,39 C180.97,39 185,34.97 185,30 C185,25.03 180.97,21 176,21 Z M176,37 C172.14,37 169,33.86 169,30 C169,26.14 172.14,23 176,23 C179.86,23 183,26.14 183,30 C183,33.86 179.86,37 176,37 Z" id="time-icon"></path></g></g></g></svg>
                    ${humanizedConsumption}
                </span>
              </div>`;
        }

        return '';
      }}
    />
  );
};

export default RewindGlobe;
